import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { Button } from '@/components/shadcn/button'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { StatusLevelModel } from '@/api/model'

interface Props {
  levels: StatusLevelModel[]
  canEdit: boolean
  onEdit: (level: StatusLevelModel) => void
  onDelete: (level: StatusLevelModel) => void
}

const formatRange = (level: StatusLevelModel): string =>
  level.maxPoints === null ? `${level.minPoints}+` : `${level.minPoints} – ${level.maxPoints}`

/**
 * Presentation table of the levels, sorted ascending by `minPoints` (a copy,
 * doesn't mutate the prop). Edit/delete only with `canEdit`.
 */
export function StatusLevelsTable({ levels, canEdit, onEdit, onDelete }: Props) {
  const { t } = useTranslation('service-modules-status')
  const sortedLevels = [...levels].sort((a, b) => a.minPoints - b.minPoints)

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">{t('columns.color')}</TableHead>
            <TableHead>{t('columns.name')}</TableHead>
            <TableHead>{t('columns.range')}</TableHead>
            <TableHead>{t('columns.payoutPoints')}</TableHead>
            {canEdit && <TableHead className="w-24 text-right">{t('columns.actions')}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          <LoaderTable isEmpty={sortedLevels.length === 0} colSpan={canEdit ? 5 : 4} emptyMessage={t('levels.empty')}>
            {sortedLevels.map((level) => (
              <TableRow key={level.id}>
                <TableCell>
                  <div
                    className="size-6 rounded-full border-2 border-border"
                    style={{ backgroundColor: level.colorHex ?? undefined }}
                  />
                </TableCell>
                <TableCell className="font-medium">{level.name}</TableCell>
                <TableCell>{formatRange(level)}</TableCell>
                <TableCell>{level.payoutPoints}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="size-8" onClick={() => onEdit(level)}>
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(level)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </LoaderTable>
        </TableBody>
      </Table>
    </div>
  )
}
